/**
 * billing-route.js — client-saas /billing (the MEASURE slice).
 *
 * Plan card + live usage meters from GET /m5t/v5/billing/usage, and (slice
 * two) Stripe: upgrade cards for Free orgs → Checkout, Manage billing →
 * Stripe's hosted Customer Portal for subscribed orgs. Honest per audience:
 * grandfathered orgs see "All features included." and NO purchase prompts
 * (they would only lose by buying); the page is self-serve-only via the nav
 * uiModes gate + the route guard in main.js. No limit enforcement anywhere
 * yet — that is slice three, new signups only.
 */

import AuthController from '../services/AuthController.js';
import { PLANS } from '@sse/shared';
import { guardedFetch } from '../core/navGeneration.js';
import { isDenial, denialState, failIfNotOk } from '../core/denialSurface.js';

const apiBase = () => AuthController.getConfig?.().BASE_URL || '';
const fmtNum = (n) => Number(n || 0).toLocaleString();
const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);

let stylesInjected = false;
function ensureStyles() {
  if (stylesInjected) return;
  const s = document.createElement('style');
  s.id = 'm5t-billing-styles';
  s.textContent = `
    .page-shell .bill-plan { display: flex; align-items: center; gap: 14px; }
    .page-shell .bill-plan__name { font-size: 1.35rem; font-weight: 700; color: var(--color-text, #0B1220); }
    .page-shell .bill-plan__badge { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
      padding: 4px 10px; border-radius: 999px; background: var(--color-primary-600, #0072BA); color: #fff; }
    .page-shell .bill-plan__sub { margin-top: 6px; font-size: 0.9rem; color: var(--color-text-muted, #4A5A78); }
    .page-shell .bill-meters { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; margin-top: 16px; }
    .page-shell .bill-meter { border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-lg, 16px); padding: 16px; background: var(--color-bg, #fff); }
    .page-shell .bill-meter__k { font-size: 0.78rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .bill-meter__v { margin-top: 6px; font-size: 1.5rem; font-weight: 700; color: var(--color-text, #0B1220); }
    .page-shell .bill-meter__v small { font-size: 0.85rem; font-weight: 600; color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .bill-bar { margin-top: 10px; height: 8px; border-radius: 999px; background: var(--color-surface-2, #EAF0F7); overflow: hidden; }
    .page-shell .bill-bar i { display: block; height: 100%; border-radius: 999px; background: var(--color-primary-600, #0072BA); }
    .page-shell .bill-bar i[data-warn="true"] { background: var(--color-danger, #DD4124); }
    .page-shell .bill-feats { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 6px 18px; margin-top: 10px; }
    .page-shell .bill-feat { display: flex; align-items: center; gap: 8px; font-size: 0.88rem; color: var(--color-text-muted, #4A5A78); }
    .page-shell .bill-feat .material-symbols-outlined { font-size: 18px; color: var(--color-success, #009473); }
    .page-shell .bill-note { font-size: 0.76rem; color: var(--color-text-subtle, #6C7C9A); margin: 14px 2px 0; }
    .page-shell .bill-meter__hint { margin-top: 8px; font-size: 0.76rem; color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .bill-plan__sub a { color: var(--color-primary-600, #0072BA); font-weight: 600; text-decoration: none; }
    .page-shell .bill-plan__sub a:hover { text-decoration: underline; }
    .page-shell .bill-feats__pro-label { margin-top: 18px; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .bill-feat--pro { color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .bill-feat--pro .material-symbols-outlined { color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .bill-btn { display: inline-flex; align-items: center; gap: 6px; border: 1px solid var(--color-border, #D4DCE8);
      background: var(--color-primary-600, #0072BA); color: #fff; border-radius: 10px; padding: 8px 14px;
      font-size: 0.85rem; font-weight: 600; cursor: pointer; }
    .page-shell .bill-btn[disabled] { opacity: 0.6; cursor: default; }
    .page-shell .bill-btn--quiet { background: var(--color-bg, #fff); color: var(--color-text, #0B1220); }
    .page-shell .bill-btn .material-symbols-outlined { font-size: 18px; }
    .page-shell .chart-period-btn { cursor: pointer; }
    .page-shell .chart-period-btn.active { background: var(--color-primary-600, #0072BA); color: #fff; }
  `;
  document.head.appendChild(s);
  stylesInjected = true;
}

const emptyState = (icon, msg) =>
  `<div class="m5t-empty"><span class="material-symbols-outlined" aria-hidden="true">${icon}</span><p>${msg}</p></div>`;

// Friendly names for the shared matrix's feature keys, in display order.
const FEATURE_LABELS = [
  ['formats', null], // rendered specially
  ['hub_scheduler', 'Welcome hub and scheduler'],
  ['ab_testing', 'A/B testing'],
  ['journeys', 'Journeys'],
  ['attribution', 'Campaign attribution'],
  ['crm_sync', 'CRM sync'],
  ['booking', 'Booking integrations'],
  ['webhooks', 'Webhooks and Zapier'],
  ['csv_export', 'CSV export'],
  ['ad_reporting', 'Ad performance reporting'],
];

export function render() {
  ensureStyles();
  return `
    <div class="page-shell" data-section="billing">
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Billing</h1>
          <p class="page-description">Your plan and usage.</p>
        </div>
      </div>
      <div class="dashboard-card">
        <div class="card-body" id="bill-plan-card">${emptyState('hourglass_empty', 'Loading your plan…')}</div>
      </div>
      <div class="bill-meters" id="bill-meters"></div>
      <div class="dashboard-card" id="bill-upgrade-card" style="display:none; margin-top:16px">
        <div class="card-header">
          <h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">upgrade</span> Upgrade</h2>
          <div class="chart-controls" id="bill-interval">
            <button class="chart-period-btn active" type="button" data-interval="monthly">Monthly</button>
            <button class="chart-period-btn" type="button" data-interval="annual">Annual</button>
          </div>
        </div>
        <div class="card-body" id="bill-upgrade"></div>
      </div>
      <div class="dashboard-card" id="bill-feat-card" style="display:none; margin-top:16px">
        <div class="card-header"><h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">checklist</span> Included in your plan</h2></div>
        <div class="card-body" id="bill-feats"></div>
      </div>
      <p class="bill-note" id="bill-note"></p>
    </div>`;
}

let billingInterval = 'monthly';
let lastData = null;

export async function init() {
  lastData = null;
  document.getElementById('bill-interval')?.addEventListener('click', (e) => {
    const btn = e.target.closest('button[data-interval]');
    if (!btn) return;
    billingInterval = btn.dataset.interval;
    document.querySelectorAll('#bill-interval .chart-period-btn').forEach((b) =>
      b.classList.toggle('active', b.dataset.interval === billingInterval));
    if (lastData) renderUpgrade(lastData);
  });
  document.getElementById('bill-upgrade-card')?.addEventListener('click', async (e) => {
    const btn = e.target.closest('button[data-plan]');
    if (!btn || !lastData || lastData.external_billing_allowed === false) return;
    btn.disabled = true;
    try {
      const res = await guardedFetch(`${apiBase()}/m5t/v5/billing/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: btn.dataset.plan, interval: billingInterval }),
      });
      const data = await res.json();
      if (!data.ok || !data.data?.url) throw new Error(data.error || 'checkout failed');
      window.location.href = data.data.url; // Stripe Checkout, back to #/billing after
    } catch (err) {
      console.warn('[billing] checkout failed:', err);
      btn.disabled = false;
      btn.textContent = 'Could not start checkout. Try again.';
    }
  });
  document.getElementById('bill-plan-card')?.addEventListener('click', async (e) => {
    const btn = e.target.closest('#bill-manage');
    if (!btn || !lastData || lastData.external_billing_allowed === false) return;
    btn.disabled = true;
    try {
      const res = await guardedFetch(`${apiBase()}/m5t/v5/billing/portal`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const data = await res.json();
      if (!data.ok || !data.data?.url) throw new Error(data.error || 'portal failed');
      window.location.href = data.data.url;
    } catch (err) {
      console.warn('[billing] portal failed:', err);
      btn.disabled = false;
    }
  });
  await load();
}

/** Upgrade cards: only for orgs on the Free plan, and only when Stripe is live. */
function renderUpgrade(data) {
  const card = document.getElementById('bill-upgrade-card');
  const body = document.getElementById('bill-upgrade');
  if (!card || !body) return;
  const show = data.plan?.key === 'free' && data.checkout_ready && data.external_billing_allowed !== false;
  card.style.display = show ? '' : 'none';
  if (!show) return;
  const tier = (p) => `
    <div class="bill-meter">
      <div class="bill-meter__k">${esc(p.label)}</div>
      <div class="bill-meter__v">$${p.prices[billingInterval]}<small>/month${billingInterval === 'annual' ? ', billed yearly' : ''}</small></div>
      <div class="bill-plan__sub">${p.key === 'pro' ? 'Unlimited magnets, all formats, 10,000 leads a month, integrations.' : 'Everything in Pro, 100,000 leads a month, unlimited team, ad reporting.'}</div>
      <div style="margin-top:12px"><button type="button" class="bill-btn" data-plan="${p.key}"><span class="material-symbols-outlined" aria-hidden="true">arrow_forward</span>Choose ${esc(p.label)}</button></div>
    </div>`;
  body.innerHTML = `<div class="bill-meters" style="margin-top:0">${tier(PLANS.pro)}${tier(PLANS.enterprise)}</div>`;
}

/** One line of plan context per audience — never a promise the page cannot
 *  keep, and never a peek behind the curtain (2026-08-26: grandfathered users
 *  are not told that published pricing exists and does not apply to them).
 *  Free gets the storefront line instead of "No charge." (2026-09-05): what
 *  the plan is FOR, then the door. Returned as HTML for the free case only,
 *  so the caller escapes everything else and trusts this one literal. */
function planSubline(plan, sub = null) {
  if (plan.key === 'enterprise' && !sub) return 'Included with your program.';
  if (plan.key === 'grandfathered') return 'All features included.';
  if (sub && plan.prices) {
    const price = sub.interval === 'annual' ? `$${plan.prices.annual}/month, billed yearly` : `$${plan.prices.monthly}/month`;
    return `${price}.`;
  }
  return '';
}

/** The Free plan card line: purpose first, upsell second. The Upgrade link
 *  scrolls to the tier cards below when checkout is live; without Stripe the
 *  same words render unlinked (an anchor to a hidden card helps nobody). */
function freeSublineHTML(checkoutReady) {
  const lead = 'Design a free magnet for your business and track its leads.';
  const offer = `for every display format, integrations, and ${fmtNum(PLANS.pro.limits.leads_mo)} leads a month.`;
  const up = checkoutReady
    ? '<a href="#/billing" id="bill-up-link">Upgrade to Pro</a>'
    : 'Upgrade to Pro';
  return `${esc(lead)} ${up} ${esc(offer)}`;
}

function meterHTML(label, value, limit, { hint = '', consumption = false } = {}) {
  const unlimited = limit == null;
  const pct = unlimited ? 0 : Math.min(100, Math.round((value / Math.max(1, limit)) * 100));
  // Only CONSUMPTION meters (leads) go red near the cap — approaching a lead
  // cap is a real warning. Allocation meters (magnets, seats) are simply full
  // at their limit; a solo Free owner sits at 1 of 1 seats forever, and a
  // permanently red bar reads as something broken. The hint carries the
  // upsell instead.
  const warn = consumption && pct >= 80;
  return `
    <div class="bill-meter">
      <div class="bill-meter__k">${esc(label)}</div>
      <div class="bill-meter__v">${fmtNum(value)}${unlimited ? ' <small>No limit</small>' : ` <small>of ${fmtNum(limit)}</small>`}</div>
      ${unlimited ? '' : `<div class="bill-bar"><i style="width:${pct}%" data-warn="${warn}"></i></div>`}
      ${hint ? `<div class="bill-meter__hint">${esc(hint)}</div>` : ''}
    </div>`;
}

async function load() {
  const planCard = document.getElementById('bill-plan-card');
  let data;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/billing/usage`);
    data = await res.json();
    failIfNotOk(res, data);
    if (!data.ok) throw new Error(data.error || 'load failed');
  } catch (err) {
    console.warn('[billing] load failed:', err);
    if (planCard) planCard.innerHTML = isDenial(err) ? denialState() : emptyState('error', 'Could not load billing. Please try again.');
    return;
  }

  const plan = data.plan || PLANS.grandfathered;
  const u = data.usage || {};
  const sub = data.subscription || null;
  lastData = data;

  if (planCard) {
    const renewal = sub?.current_period_end
      ? `${sub.cancel_at_period_end ? 'Ends' : 'Renews'} ${new Date(sub.current_period_end).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}.`
      : '';
    const pastDue = sub?.status === 'PAST_DUE'
      ? ' <span class="bill-plan__badge" style="background: var(--color-danger, #DD4124)">Payment needed</span>' : '';
    planCard.innerHTML = `
      <div class="bill-plan">
        <span class="bill-plan__name">${esc(plan.label)}</span>
        <span class="bill-plan__badge">Current plan</span>${pastDue}
        ${sub && data.external_billing_allowed!==false ? '<span style="flex:1 1 auto"></span><button type="button" class="bill-btn bill-btn--quiet" id="bill-manage"><span class="material-symbols-outlined" aria-hidden="true">credit_card</span>Manage billing</button>' : ''}
      </div>
      <div class="bill-plan__sub">${data.external_billing_allowed===false
        ? data.billing_route==='shopify'
          ? 'Open Mach Five Magnet in Shopify to choose or manage your plan. <a id="bill-shopify-integrations" href="#/settings/integrations">View integrations</a>'
          : esc('Your billing connection needs review. Contact Mach Five before changing your subscription; existing billing has not been changed.')
        : plan.key === 'free'
        ? freeSublineHTML(!!data.checkout_ready)
        : esc([planSubline(plan, sub), renewal].filter(Boolean).join(' '))}</div>`;
    planCard.querySelector('#bill-up-link')?.addEventListener('click', (e) => {
      e.preventDefault();
      document.getElementById('bill-upgrade-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  renderUpgrade(data);

  const meters = document.getElementById('bill-meters');
  if (meters) {
    const free = plan.key === 'free';
    meters.innerHTML = [
      meterHTML('Live magnets', u.magnets, plan.limits?.magnets ?? null,
        { hint: free ? 'Pro includes unlimited magnets.' : '' }),
      meterHTML('Leads this month', u.leads_month, plan.limits?.leads_mo ?? null,
        { consumption: true, hint: free ? `Pro includes ${fmtNum(PLANS.pro.limits.leads_mo)} a month.` : '' }),
      meterHTML('Team members', u.team_members, plan.limits?.team_members ?? null,
        { hint: free ? `Pro includes ${fmtNum(PLANS.pro.limits.team_members)} seats.` : '' }),
    ].join('');
  }

  const featCard = document.getElementById('bill-feat-card');
  const feats = document.getElementById('bill-feats');
  if (featCard && feats) {
    const items = [];
    const formats = plan.features?.formats || [];
    items.push(`<div class="bill-feat"><span class="material-symbols-outlined" aria-hidden="true">check_circle</span>${formats.length >= 4 ? 'All four display formats' : 'Chat bubble format'}</div>`);
    for (const [key, label] of FEATURE_LABELS) {
      if (!label) continue;
      if (plan.features?.[key]) items.push(`<div class="bill-feat"><span class="material-symbols-outlined" aria-hidden="true">check_circle</span>${esc(label)}</div>`);
    }
    if (['pro', 'enterprise'].includes(plan.key)) items.push('<div class="bill-feat"><span class="material-symbols-outlined" aria-hidden="true">check_circle</span>Shopify product guides and catalog integration</div>');
    // Free orgs also see what Pro adds, muted, under its own label. Built
    // from the plan matrix, so the teaser can never drift from what Pro
    // actually includes. Ad reporting stays out: it belongs to Enterprise,
    // and the upgrade card above already names it there.
    let proTeaser = '';
    if (plan.key === 'free') {
      const proItems = ['Shopify product guides and catalog integration'];
      if ((plan.features?.formats || []).length < 4 && (PLANS.pro.features.formats || []).length >= 4) {
        proItems.push('All four display formats');
      }
      for (const [key, label] of FEATURE_LABELS) {
        if (!label) continue;
        if (!plan.features?.[key] && PLANS.pro.features?.[key]) proItems.push(label);
      }
      if (proItems.length) {
        proTeaser = `<div class="bill-feats__pro-label">Included with Pro</div>
          <div class="bill-feats">${proItems.map((l) =>
            `<div class="bill-feat bill-feat--pro"><span class="material-symbols-outlined" aria-hidden="true">add_circle</span>${esc(l)}</div>`).join('')}</div>`;
      }
    }
    feats.innerHTML = `<div class="bill-feats">${items.join('')}</div>${proTeaser}`;
    featCard.style.display = '';
  }

  const note = document.getElementById('bill-note');
  if (note) {
    note.textContent = plan.key === 'enterprise'
      ? 'Usage is informational. Your program covers the platform.'
      : `Lead counts reset monthly. This period: ${data.period?.start || ''} to ${data.period?.end || ''}.`;
  }
}
